import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { subscriptionAPI } from '../../../../services/api';
import Alert from '../../../../components/common/Alert';
import Button from '../../../../components/common/Button';
import Card from '../../../../components/common/Card';
import { Users } from 'lucide-react';

// Initialize Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

// Stripe Card Element styles
const cardStyle = {
  style: {
    base: {
      fontSize: '16px',
      color: '#1C3F45', // Midnight color
      '::placeholder': {
        color: '#5D695F', // Ebony color
      },
    },
    invalid: {
      color: '#FF6666', // Bittersweet color
    },
  },
};

const PaymentFormContent = ({ plan, billingPeriod, onClose, currentSubscription }) => {
  const stripe = useStripe();
  const elements = useElements();
  
  // Check if this is a seat addition operation
  const isSeatAddition = plan._seatAddMode === true;
  
  // Use the appropriate seat count based on context
  const [seats, setSeats] = useState(
    isSeatAddition 
      ? plan._additionalSeats 
      : (currentSubscription?.seats_allocated || 1)
  );
  
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Calculate the total without any discount.
  const calculateTotal = () => {
    // If it's a seat addition with prorated amount, use that
    if (isSeatAddition && plan._proratedAmount) {
      return plan._proratedAmount;
    }
    
    // Otherwise calculate based on plan and seats
    const basePrice = billingPeriod === 'yearly' ? plan.yearly_price : plan.monthly_price;
    return (basePrice * seats) / 100;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setProcessing(true);
    setError(null);
  
    if (!stripe || !elements) {
      setError('Stripe has not been initialized');
      setProcessing(false);
      return;
    }
  
    try {
      // Create payment method
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
      });
  
      if (stripeError) {
        throw new Error(stripeError.message);
      }
  
      // Handle different operations based on context
      if (isSeatAddition) {
        // This is a seat addition operation
        try {
          // Create a specialized API call for adding seats with a new payment method
          const response = await subscriptionAPI.addSeatsWithPayment(
            currentSubscription.organization_id,
            currentSubscription.id,
            {
              paymentMethodId: paymentMethod.id,
              additionalSeats: plan._additionalSeats,
              savePaymentMethod: true // Optionally save this payment method for future charges
            }
          );
          console.log('Seats added with payment:', response);
          
          setSuccess(true);
          setTimeout(() => {
            onClose();
            window.location.reload();
          }, 2000);
        } catch (apiError) {
          console.error('Seat addition API error:', apiError);
          throw new Error(apiError.response?.data?.message || 'Failed to add seats');
        }
      } else {
        // This is a regular subscription operation
        const subscriptionData = {
          planId: plan.id,
          paymentMethodId: paymentMethod.id,
          billingPeriod,
          seatsAllocated: seats,
        };
        
        try {
          if (currentSubscription && currentSubscription.id) {
            // Update existing subscription
            const response = await subscriptionAPI.updateSubscription(
              currentSubscription.organization_id,
              currentSubscription.id,
              subscriptionData
            );
            console.log('Subscription updated:', response);
          } else {
            // Create new subscription
            const response = await subscriptionAPI.createSubscription(
              currentSubscription.organization_id,
              subscriptionData
            );
            console.log('Subscription created:', response);
          }
          
          setSuccess(true);
          setTimeout(() => {
            onClose();
            window.location.reload();
          }, 2000);
        } catch (apiError) {
          console.error('Subscription API error:', apiError);
          throw new Error(apiError.response?.data?.message || 'Failed to process subscription');
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-midnight/70 backdrop-blur-sm flex items-center justify-center z-50">
      <Card className="w-full max-w-md mx-4 p-6 rounded-2xl">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-midnight font-heading flex items-center">
            {isSeatAddition ? (
              <>
                <Users className="w-6 h-6 mr-2 text-violet" />
                Add Seats to Subscription
              </>
            ) : (
              <>
                {currentSubscription?.id ? 'Update Subscription' : 'Start Subscription'}
              </>
            )}
          </h2>
          <button
            onClick={onClose}
            className="text-ebony hover:text-midnight hover:bg-gray-100 p-1 rounded-full transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <Alert type="error" className="mb-4">
            {error}
          </Alert>
        )}

        {success && (
          <Alert type="success" className="mb-4">
            {isSeatAddition 
              ? `Your seats have been added successfully.` 
              : `Your subscription has been ${currentSubscription?.id ? 'updated' : 'created'} successfully.`
            }
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {!isSeatAddition && (
            <div>
              <label className="block text-sm font-medium text-midnight mb-1">
                Number of Seats
              </label>
              <input
                type="number"
                min="1"
                step="1"
                value={seats}
                onChange={(e) => setSeats(Number(e.target.value))}
                className="w-full px-3 py-2 border-2 border-light-purple rounded-xl focus:outline-none focus:ring-2 focus:ring-violet focus:border-violet transition-all"
              />
              <p className="text-sm text-ebony mt-1">
                Minimum 1 seat
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-midnight mb-1">
              Card Details
            </label>
            <div className="border-2 border-light-purple rounded-xl p-3 hover:border-violet focus-within:border-violet focus-within:ring-2 focus-within:ring-violet/20 transition-all">
              <CardElement options={cardStyle} />
            </div>
            <p className="text-xs text-ebony mt-1">
              Your card information is securely processed by Stripe.
            </p>
          </div>

          <Card className="bg-old-lace/30 border border-old-lace">
            <h4 className="font-medium mb-2 text-midnight">Summary</h4>
            <div className="space-y-1 text-sm">
              <p className="text-ebony">Plan: <span className="text-midnight font-medium">{plan.name}</span></p>
              
              {isSeatAddition ? (
                <>
                  <p className="text-ebony">
                    Additional Seats: <span className="text-midnight font-medium">{plan._additionalSeats}</span>
                  </p>
                  <p className="text-ebony">
                    Price per seat: <span className="text-midnight font-medium">
                      ${((currentSubscription.billing_period === 'yearly' 
                        ? plan.price_per_seat_yearly 
                        : plan.price_per_seat_monthly) / 100).toFixed(2)}
                    </span>
                  </p>
                  <p className="text-ebony">
                    Prorated Amount: <span className="text-midnight font-medium">
                      ${plan._proratedAmount.toFixed(2)}
                    </span>
                  </p>
                </>
              ) : (
                <>
                  <p className="text-ebony">Billing Period: <span className="text-midnight font-medium">{billingPeriod}</span></p>
                  <p className="text-ebony">Seats: <span className="text-midnight font-medium">{seats}</span></p>
                  <p className="text-ebony">
                    Price per seat: <span className="text-midnight font-medium">
                      ${((billingPeriod === 'yearly' ? plan.yearly_price : plan.monthly_price) / 100).toFixed(2)}
                    </span>
                  </p>
                </>
              )}
              
              <p className="text-lg font-bold mt-2 text-midnight flex items-center justify-between">
                <span>Total:</span> 
                <span>
                  ${calculateTotal().toFixed(2)}
                  {!isSeatAddition && `/${billingPeriod === 'monthly' ? 'mo' : 'yr'}`}
                </span>
              </p>
            </div>
          </Card>

          <div className="flex justify-end space-x-4">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={processing}
              className="rounded-xl border-2"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={processing || !stripe}
              className="rounded-xl bg-violet hover:bg-violet/90"
            >
              {processing ? 'Processing...' : isSeatAddition ? 'Add Seats' : currentSubscription?.id ? 'Update Subscription' : 'Start Subscription'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

const PaymentForm = (props) => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentFormContent {...props} />
    </Elements>
  );
};

export default PaymentForm;